import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  TextField,
  Grid,
  Card,
  CardHeader,
  Avatar,
  Typography,
  FormHelperText,
  Autocomplete,
  Box,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import closeIcon from '../../../../assets/accountSetting-assets/x-circle.svg';
import {
  ExpireTimeLabelTypo,
  RoleLabelTypo,
  SalesAdminLabelTypo,
  MainSectionCardContent,
  classes,
} from './utils';
import {
  ADD_TO_TEAM_SA,
  ERROR,
  INVALID_INPUT_MESSAGES,
  INVALID_INPUT_REGEX,
  UTILS,
} from '../../../../constants/staticContent';
import {
  getSalesAdminList,
  inviteNewMembersSASide,
} from '../../../../services/addNewMembersSASideApi';
import ExpiryTimeDropdown from '../../utils/ExpiryTimeDropdown';
import { getLocalStorageItem } from '../../../../services/localStorageUtils';
import ROLES from '../../../../constants/roles';

const AddNewMembersSASide = ({
  handleClose,
  setInvitationEmail,
  invitationEmail,
  emailError,
  setEmailError,
  roleError,
  setRoleError,
  selectedRole,
  setSelectedRole,
  selectedExpiration,
  handleExpireTimeValue,
}) => {
  const role = getLocalStorageItem('role');
  let roles = [];

  if (role === ROLES.SUPER_ADMIN) {
    roles = [
      { title: 'Super Manager', role: 'SUPER_MANAGER' },
      { title: 'Sales Admin', role: 'SALES_ADMIN' },
      { title: 'Sales', role: 'SALES' },
      { title: 'Support', role: 'SUPPORT' },
    ];
  } else if (role === ROLES.SUPER_MANAGER) {
    roles = [
      { title: 'Sales Admin', role: 'SALES_ADMIN' },
      { title: 'Sales', role: 'SALES' },
      { title: 'Support', role: 'SUPPORT' },
    ];
  } else if (role === ROLES.SALES_ADMIN) {
    roles = [{ title: 'Sales', role: 'SALES' }];
  }
  const [isLoading, setIsLoading] = useState(false);
  const [salesAdminList, setSalesAdminList] = useState([]);
  const [selectedSalesAdmin, setSelectedSalesAdmin] = useState(null);
  const [isSalesAdminListFetching, setIsSalesAdminListFetching] =
    useState(false);
  const [salesAdminError, setSalesAdminError] = useState('');

  const handleRole = (newValue) => {
    setSelectedRole(newValue);
    setRoleError(null);
    if (!newValue) {
      setSelectedSalesAdmin(null);
      setSalesAdminError('');
    }
  };
  const getEmailError = (email) => {
    const emailRegex = INVALID_INPUT_REGEX.EMAIL;
    const isFirstLatterCapital = INVALID_INPUT_REGEX.EMAIL_CAPITAL.test(email);
    if (email === '') {
      return ERROR.EMPTY_ERROR;
    }
    if (!emailRegex.test(email) || !isFirstLatterCapital) {
      return INVALID_INPUT_MESSAGES.EMAIL;
    }

    return '';
  };

  const inviteOrganizationHandler = async () => {
    const emailValidation = getEmailError(invitationEmail);
    if (emailValidation && selectedRole === null) {
      setEmailError(emailValidation);
      setRoleError(INVALID_INPUT_MESSAGES.ROLE);
    } else if (emailValidation) {
      setEmailError(emailValidation);
    } else if (selectedRole === null) {
      setRoleError(INVALID_INPUT_MESSAGES.ROLE);
    } else if (selectedRole?.role === ROLES.SALES && !selectedSalesAdmin) {
      setSalesAdminError(INVALID_INPUT_MESSAGES.SALES_ADMIN);
    } else {
      setIsLoading(true);
      const expiryTime = selectedExpiration?.time;
      const response = await inviteNewMembersSASide(
        invitationEmail,
        selectedRole?.role,
        selectedSalesAdmin?.id,
        expiryTime,
      );
      if (response) {
        setIsLoading(false);
        handleClose();
      } else {
        setIsLoading(false);
      }
    }
  };

  const fetchSalesAdminList = async () => {
    setIsSalesAdminListFetching(true);
    const finalList = await getSalesAdminList();
    if (finalList) {
      setSalesAdminList(finalList);
      setIsSalesAdminListFetching(false);
    }
  };

  useEffect(() => {
    if (selectedRole?.role === 'SALES') {
      fetchSalesAdminList();
    }
  }, [selectedRole]);
  return (
    <Card
      className={
        emailError || roleError
          ? classes.errorCardComponent
          : classes.cardComponent
      }
    >
      <CardHeader
        className={classes.cardHeader}
        title={
          <Grid container alignItems="center">
            <Grid item xs={10}>
              <Typography variant="h6">{ADD_TO_TEAM_SA.CARD_TITLE}</Typography>
            </Grid>
            <Grid item xs={2} align="right">
              <Avatar
                onClick={handleClose}
                src={closeIcon}
                className={classes.closeAction}
              />
            </Grid>
          </Grid>
        }
      />

      <Box className={classes.cardContent}>
        <Typography variant="subtitle1" className={classes.subTitle}>
          {ADD_TO_TEAM_SA.SUB_TITLE}
        </Typography>
        <MainSectionCardContent>
          <Typography
            variant="subtitle1"
            className={classes.emailTextFieldText}
          >
            {ADD_TO_TEAM_SA.EMAIL}
          </Typography>

          <TextField
            fullWidth
            size="small"
            id="outlined-basic"
            variant="outlined"
            className={classes.textField}
            value={invitationEmail}
            error={Boolean(emailError)}
            helperText={emailError}
            onChange={(event) => {
              const email = event.target.value;
              setInvitationEmail(email);
              setEmailError(getEmailError(email));
            }}
          />

          {(role === ROLES.SUPER_ADMIN ||
            role === ROLES.SUPER_MANAGER ||
            role === ROLES.SALES_ADMIN) && (
            <>
              <RoleLabelTypo variant="subtitle1">
                {ADD_TO_TEAM_SA.ROLE}
              </RoleLabelTypo>

              <Autocomplete
                fullWidth
                value={selectedRole}
                options={roles}
                size="small"
                getOptionLabel={(option) => option.title}
                onChange={(event, newValue) => {
                  handleRole(newValue);
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option}>
                    <Typography>{option.title}</Typography>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} error={Boolean(roleError)} />
                )}
              />
              {roleError && (
                <FormHelperText className={classes.formHelperText}>
                  {roleError}
                </FormHelperText>
              )}
            </>
          )}

          {selectedRole?.role === ROLES.SALES && (
            <>
              <SalesAdminLabelTypo variant="subtitle1">
                {ADD_TO_TEAM_SA.SALES_ADMIN}
              </SalesAdminLabelTypo>

              <Autocomplete
                fullWidth
                value={selectedSalesAdmin}
                options={salesAdminList}
                loading={isSalesAdminListFetching}
                size="small"
                getOptionLabel={(option) => option.email}
                onChange={(event, newValue) => {
                  setSelectedSalesAdmin(newValue);
                  setSalesAdminError('');
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option}>
                    <Typography>{option.email}</Typography>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} error={Boolean(salesAdminError)} />
                )}
              />
              {salesAdminError && (
                <FormHelperText className={classes.formHelperText}>
                  {salesAdminError}
                </FormHelperText>
              )}
            </>
          )}

          <ExpireTimeLabelTypo variant="subtitle1">
            {UTILS.EXPIRY_TIME}
          </ExpireTimeLabelTypo>

          <ExpiryTimeDropdown
            selectedExpiration={selectedExpiration}
            handleExpireTimeValue={handleExpireTimeValue}
          />
        </MainSectionCardContent>
        <LoadingButton
          variant="contained"
          alignItems="center"
          className={classes.addUserBtn}
          onClick={inviteOrganizationHandler}
          loading={isLoading}
        >
          {ADD_TO_TEAM_SA.INVITE_BUTTON}
        </LoadingButton>
      </Box>
    </Card>
  );
};

AddNewMembersSASide.defaultProps = {
  roleError: null,
};

AddNewMembersSASide.propTypes = {
  handleClose: PropTypes.func.isRequired,
  setInvitationEmail: PropTypes.func.isRequired,
  invitationEmail: PropTypes.string.isRequired,
  emailError: PropTypes.string.isRequired,
  setEmailError: PropTypes.func.isRequired,
  roleError: PropTypes.string,
  selectedRole: PropTypes.string.isRequired,
  setSelectedRole: PropTypes.func.isRequired,
  setRoleError: PropTypes.func.isRequired,
  selectedExpiration: PropTypes.shape({
    title: PropTypes.string.isRequired,
    time: PropTypes.string.isRequired,
  }).isRequired,
  handleExpireTimeValue: PropTypes.func.isRequired,
};

export default AddNewMembersSASide;
